import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { DataTable } from "@/modules/shell/data-table";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  createAccount,
  deactivateAccount,
  listAccounts,
  updateAccount,
  type GlAccount,
} from "@/shared/api/accounting";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { chartOfAccountsColumns } from "./chart-of-accounts-columns";

const ACCOUNT_TYPES = [
  "asset",
  "liability",
  "equity",
  "income",
  "expense",
] as const;

const accountCreateSchema = z.object({
  code: z.string().trim().min(1, "Code is required"),
  name: z.string().trim().min(1, "Name is required"),
  type: z.enum(ACCOUNT_TYPES),
});
type AccountCreateInput = z.input<typeof accountCreateSchema>;

/** Advanced: rename / add COA — plan/00, plan/09 (starter chart remains) */
export function ChartOfAccountsPage() {
  const { companySlug } = useParams();
  const confirmUi = useConfirm();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const accountsQuery = useQuery({
    queryKey: queryKeys.gl.accounts(companySlug ?? "", {
      includeDeactivated: true,
    }),
    enabled: Boolean(companySlug),
    queryFn: () =>
      listAccounts(companySlug!, { includeDeactivated: true }),
  });
  const errorMsg = queryErrorMessage(
    accountsQuery.error,
    accountsQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const createForm = useForm<AccountCreateInput>({
    resolver: zodResolver(accountCreateSchema),
    defaultValues: { code: "", name: "", type: "expense" },
  });

  async function reload() {
    if (!companySlug) return;
    await queryClient.invalidateQueries({
      queryKey: ["gl", companySlug, "accounts"],
    });
  }

  const create = useMutation({
    mutationFn: async (raw: AccountCreateInput) => {
      const body = accountCreateSchema.parse(raw);
      return createAccount(companySlug!, body);
    },
    onSuccess: async () => {
      createForm.reset({ code: "", name: "", type: "expense" });
      setError(null);
      await reload();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Create failed");
    },
  });

  const columns = useMemo(
    () =>
      companySlug
        ? chartOfAccountsColumns({
            companySlug,
            busy,
            onRename: (a: GlAccount, nextName: string) => {
              const trimmed = nextName.trim();
              if (!trimmed || trimmed === a.name) return;
              setBusy(true);
              setError(null);
              void updateAccount(companySlug, a.id, {
                name: trimmed,
                ...(a.deactivatedAt ? { reactivate: true } : {}),
              })
                .then(() => reload())
                .catch((err: unknown) =>
                  setError(
                    err instanceof Error ? err.message : "Update failed",
                  ),
                )
                .finally(() => setBusy(false));
            },
            onDeactivate: (a: GlAccount) => {
              void (async () => {
                if (a.isSystem) return;
                const ok = await confirmUi({
                  title: `Hide ${a.code}?`,
                  description: `${a.name} will be hidden from new picks.`,
                  confirmLabel: "Hide",
                  destructive: true,
                });
                if (!ok) return;
                setBusy(true);
                setError(null);
                try {
                  await deactivateAccount(companySlug, a.id);
                  await reload();
                } catch (err: unknown) {
                  setError(err instanceof Error ? err.message : "Failed");
                } finally {
                  setBusy(false);
                }
              })();
            },
            onReactivate: (a: GlAccount) => {
              setBusy(true);
              setError(null);
              void updateAccount(companySlug, a.id, { reactivate: true })
                .then(() => reload())
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : "Failed"),
                )
                .finally(() => setBusy(false));
            },
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match CustomersPage pattern
    [busy, companySlug, confirmUi],
  );

  if (!companySlug) return null;

  const accounts = accountsQuery.data?.accounts ?? [];
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  return (
    <PageFrame>
      <PageHeader
        title="Chart of accounts"
        description="Starter chart posts sales and purchases without setup. Advanced: rename labels or add accounts. System codes stay fixed for posting."
      />

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      <form
        className="mt-8 space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/80 p-4 shadow-[0_1px_0_color-mix(in_oklab,var(--color-foreground)_4%,transparent)]"
        onSubmit={createForm.handleSubmit((values) => create.mutate(values))}
      >
        <h2 className="m-0 text-sm font-semibold">Add account</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block text-sm">
            <span className="text-[var(--color-muted)]">Code</span>
            <Input
              className="mt-1 font-mono"
              placeholder="5200"
              {...createForm.register("code")}
            />
            {createForm.formState.errors.code && (
              <p className="mt-1 text-xs text-[var(--color-destructive)]">
                {createForm.formState.errors.code.message}
              </p>
            )}
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="text-[var(--color-muted)]">Name</span>
            <Input
              className="mt-1"
              placeholder="Operating expense"
              {...createForm.register("name")}
            />
            {createForm.formState.errors.name && (
              <p className="mt-1 text-xs text-[var(--color-destructive)]">
                {createForm.formState.errors.name.message}
              </p>
            )}
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-[var(--color-muted)]">Type</span>
          <Select
            className="mt-1 max-w-xs"
            {...createForm.register("type")}
          >
            {ACCOUNT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </label>
        <Button type="submit" disabled={busy || create.isPending}>
          {create.isPending ? "Saving…" : "Add"}
        </Button>
      </form>

      {accountsQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading accounts…
        </Alert>
      )}

      {!accountsQuery.isPending && accounts.length > 0 ? (
        <DataTable
          listId="chart-of-accounts"
          companySlug={companySlug}
          columns={columns}
          data={accounts}
          getRowId={(r) => r.id}
          manualSorting={false}
        />
      ) : null}

      <p className="mt-8 text-sm">
        <Link
          to={`/c/${companySlug}/gl/trial-balance`}
          className="no-underline"
        >
          ← Trial balance
        </Link>
      </p>
    </PageFrame>
  );
}
